import { httpClient, toClientApiError } from "./httpClient";

export type CashIncome = {
  id: string;
  customerId: string;
  title: string;
  amountAgorot: number;
  amountPaidAgorot: number;
  currency: string;
  status: "draft" | "completed" | "partial" | "cancelled";
  categoryId: string | null;
  fundId: string | null;
  paymentMethod: string | null;
  reference: string | null;
  counterpartyName: string | null;
  incomeDate: string;
  notes: string | null;
  receiptSource?: string;
  receiptExternalRef?: string | null;
};

export type CashExpense = {
  id: string;
  customerId: string | null;
  title: string;
  amountAgorot: number;
  amountPaidAgorot: number;
  currency: string;
  status: "recorded" | "cancelled";
  book: "ar" | "cash";
  categoryId: string | null;
  fundId: string | null;
  supplierId: string | null;
  paymentMethod: string | null;
  incurredAt: string;
  notes: string | null;
};

export type CashTransaction = {
  id: string;
  kind: string;
  book: string;
  amountAgorot: number;
  currency: string;
  occurredAt: string;
  note: string | null;
  incomeId: string | null;
  expenseId: string | null;
  fundId: string | null;
};

export type FundBalance = {
  id: string;
  fundId: string;
  amountAgorot: number;
  currency: string;
};

export type FinanceSupplier = {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
};

export type PaymentRequest = {
  id: string;
  title: string;
  amountAgorot: number;
  status: string;
  supplierId: string | null;
  expenseId: string | null;
  notes: string | null;
};

export type FinanceDocument = {
  id: string;
  title: string;
  source: string;
  storageKey: string | null;
  externalUrl: string | null;
  notes: string | null;
};

export type FinanceReceipt = {
  id: string;
  title: string;
  amountAgorot: number | null;
  incomeId: string | null;
  expenseId: string | null;
  source: string;
  externalRef: string | null;
};

export type BankLine = {
  id: string;
  statementDate: string;
  description: string;
  amountAgorot: number;
  status: "unmatched" | "matched" | "ignored";
};

export type AccountingDocument = {
  id: string;
  title: string;
  documentType: string;
  externalId: string | null;
  pdfUrl: string | null;
};

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

function path(customerId: string, suffix: string): string {
  return `/customers/${customerId}${suffix}`;
}

async function postMultipart<T>(url: string, file: File): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  const res = await httpClient.post<T>(url, form, {
    headers: { "Content-Type": undefined },
    transformRequest: [
      (data, headers) => {
        if (headers && typeof headers === "object" && "delete" in headers) {
          (headers as { delete: (key: string) => void }).delete("Content-Type");
        } else if (headers && typeof headers === "object") {
          delete (headers as Record<string, unknown>)["Content-Type"];
          delete (headers as Record<string, unknown>)["content-type"];
        }
        return data;
      },
    ],
  });
  return res.data;
}

export const cashLedgerApi = {
  listIncomes(customerId: string) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: CashIncome[] }>(
        path(customerId, "/incomes"),
      );
      return res.data.items;
    });
  },

  createIncome(
    customerId: string,
    body: {
      title: string;
      amountAgorot: number;
      amountPaidAgorot?: number;
      fundId?: string | null;
      categoryId?: string | null;
      paymentMethod?: string | null;
      reference?: string | null;
      counterpartyName?: string | null;
      incomeDate?: string;
      notes?: string | null;
      affectsFund?: boolean;
      issueViaEzcount?: boolean;
    },
  ) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: CashIncome }>(
        path(customerId, "/incomes"),
        body,
      );
      return res.data.item;
    });
  },

  payIncome(customerId: string, incomeId: string, amountAgorot: number) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: CashIncome }>(
        path(customerId, `/incomes/${incomeId}/pay`),
        { amountAgorot },
      );
      return res.data.item;
    });
  },

  cancelIncome(customerId: string, incomeId: string) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: CashIncome }>(
        path(customerId, `/incomes/${incomeId}/cancel`),
        {},
      );
      return res.data.item;
    });
  },

  listCashExpenses(customerId: string) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: CashExpense[] }>(
        path(customerId, "/cash-expenses"),
      );
      return res.data.items;
    });
  },

  createCashExpense(
    customerId: string,
    body: {
      title: string;
      amountAgorot: number;
      fundId?: string | null;
      categoryId?: string | null;
      supplierId?: string | null;
      paymentMethod?: string | null;
      incurredAt?: string;
      notes?: string | null;
      affectsFund?: boolean;
    },
  ) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: CashExpense }>(
        path(customerId, "/cash-expenses"),
        body,
      );
      return res.data.item;
    });
  },

  cancelCashExpense(customerId: string, expenseId: string) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: CashExpense }>(
        path(customerId, `/cash-expenses/${expenseId}/cancel`),
        {},
      );
      return res.data.item;
    });
  },

  listTransactions(customerId: string) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: CashTransaction[] }>(
        path(customerId, "/cash-transactions"),
      );
      return res.data.items;
    });
  },

  listFundBalances(customerId: string) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: FundBalance[] }>(
        path(customerId, "/fund-balances"),
      );
      return res.data.items;
    });
  },

  listSuppliers(customerId: string) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: FinanceSupplier[] }>(
        path(customerId, "/suppliers"),
      );
      return res.data.items;
    });
  },

  createSupplier(customerId: string, body: { name: string; phone?: string | null }) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: FinanceSupplier }>(
        path(customerId, "/suppliers"),
        body,
      );
      return res.data.item;
    });
  },

  listPaymentRequests(customerId: string) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: PaymentRequest[] }>(
        path(customerId, "/payment-requests"),
      );
      return res.data.items;
    });
  },

  createPaymentRequest(
    customerId: string,
    body: { title: string; amountAgorot: number; notes?: string | null },
  ) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: PaymentRequest }>(
        path(customerId, "/payment-requests"),
        body,
      );
      return res.data.item;
    });
  },

  convertPaymentRequest(customerId: string, requestId: string) {
    return wrap(async () => {
      const res = await httpClient.post<{
        request: PaymentRequest;
        expense: CashExpense | null;
      }>(path(customerId, `/payment-requests/${requestId}/convert`), {});
      return res.data;
    });
  },

  listDocuments(customerId: string) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: FinanceDocument[] }>(
        path(customerId, "/finance-documents"),
      );
      return res.data.items;
    });
  },

  createDocument(customerId: string, body: { title: string; notes?: string | null }) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: FinanceDocument }>(
        path(customerId, "/finance-documents"),
        body,
      );
      return res.data.item;
    });
  },

  listReceipts(customerId: string) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: FinanceReceipt[] }>(
        path(customerId, "/receipts"),
      );
      return res.data.items;
    });
  },

  createReceipt(
    customerId: string,
    body: {
      title: string;
      amountAgorot?: number | null;
      incomeId?: string | null;
      issueViaEzcount?: boolean;
    },
  ) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: FinanceReceipt }>(
        path(customerId, "/receipts"),
        body,
      );
      return res.data.item;
    });
  },

  incomeReceiptEligibility(customerId: string, incomeId: string) {
    return wrap(async () => {
      const res = await httpClient.get<{
        item: { allowed: boolean; code?: string; message?: string };
      }>(path(customerId, `/incomes/${incomeId}/receipt-eligibility`));
      return res.data.item;
    });
  },

  issueIncomeReceipt(
    customerId: string,
    incomeId: string,
    body?: { force?: boolean; docType?: number | null },
  ) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: unknown }>(
        path(customerId, `/incomes/${incomeId}/issue-receipt`),
        body ?? {},
      );
      return res.data.item;
    });
  },

  listBankLines(customerId: string) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: BankLine[] }>(
        path(customerId, "/bank-lines"),
      );
      return res.data.items;
    });
  },

  importBankLines(
    customerId: string,
    lines: { statementDate: string; description: string; amountAgorot: number }[],
  ) {
    return wrap(async () => {
      const res = await httpClient.post<{
        count: number;
        items: BankLine[];
      }>(path(customerId, "/bank-lines/import"), { lines });
      return res.data;
    });
  },

  previewBankImportFile(customerId: string, file: File) {
    return wrap(async () =>
      postMultipart<{
        totalRows: number;
        estimatedCreate: number;
        estimatedSkip: number;
        unmappedHeaders: string[];
        errors: Array<{ line: number; message: string }>;
        sample: Array<Record<string, unknown>>;
        lines: Array<{
          statementDate: string;
          description: string;
          amountAgorot: number;
        }>;
      }>(path(customerId, "/bank-lines/import-file/preview"), file),
    );
  },

  commitBankImportFile(
    customerId: string,
    lines: Array<{
      statementDate: string;
      description: string;
      amountAgorot: number;
    }>,
  ) {
    return wrap(async () => {
      const res = await httpClient.post<{ count: number; items: BankLine[] }>(
        path(customerId, "/bank-lines/import-file/commit"),
        { lines },
      );
      return res.data;
    });
  },

  previewIncomeImportFile(customerId: string, file: File) {
    return wrap(async () =>
      postMultipart<{
        totalRows: number;
        estimatedCreate: number;
        estimatedSkip: number;
        unmappedHeaders: string[];
        errors: Array<{ line: number; message: string }>;
        sample: Array<Record<string, unknown>>;
        rows: Array<{
          title: string;
          amountAgorot: number;
          date?: string;
          notes?: string | null;
        }>;
      }>(path(customerId, "/incomes/import/preview"), file),
    );
  },

  commitIncomeImportFile(
    customerId: string,
    rows: Array<{
      title: string;
      amountAgorot: number;
      date?: string;
      notes?: string | null;
    }>,
  ) {
    return wrap(async () => {
      const res = await httpClient.post<{ count: number }>(
        path(customerId, "/incomes/import/file-commit"),
        { rows },
      );
      return res.data;
    });
  },

  previewExpenseImportFile(customerId: string, file: File) {
    return wrap(async () =>
      postMultipart<{
        totalRows: number;
        estimatedCreate: number;
        estimatedSkip: number;
        unmappedHeaders: string[];
        errors: Array<{ line: number; message: string }>;
        sample: Array<Record<string, unknown>>;
        rows: Array<{
          title: string;
          amountAgorot: number;
          date?: string;
          notes?: string | null;
        }>;
      }>(path(customerId, "/cash-expenses/import/preview"), file),
    );
  },

  commitExpenseImportFile(
    customerId: string,
    rows: Array<{
      title: string;
      amountAgorot: number;
      date?: string;
      notes?: string | null;
    }>,
  ) {
    return wrap(async () => {
      const res = await httpClient.post<{ count: number }>(
        path(customerId, "/cash-expenses/import/file-commit"),
        { rows },
      );
      return res.data;
    });
  },

  matchBankLine(
    customerId: string,
    lineId: string,
    status: "matched" | "ignored" | "unmatched",
  ) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: BankLine }>(
        path(customerId, `/bank-lines/${lineId}/match`),
        { status },
      );
      return res.data.item;
    });
  },

  listAccountingDocuments(customerId: string) {
    return wrap(async () => {
      const res = await httpClient.get<{
        items: AccountingDocument[];
        ezcountDocuments?: unknown[];
      }>(path(customerId, "/accounting-documents"));
      return res.data;
    });
  },

  createAccountingDocument(
    customerId: string,
    body: { title: string; documentType?: string; importUrlOrUuid?: string },
  ) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: AccountingDocument }>(
        path(customerId, "/accounting-documents"),
        body,
      );
      return res.data.item;
    });
  },
};

export function formatShekels(agorot: number): string {
  return (agorot / 100).toLocaleString("he-IL", {
    style: "currency",
    currency: "ILS",
  });
}
